import { useState } from "react";
import { Link } from "react-router-dom";
import { Pager } from "@/app/Pager";
import { accountBindStatus } from "@/app/binds";
import { ClearFilters } from "@/app/ClearFilters";
import { BindStatusPill } from "@/app/BindStatusPill";
import { useCanWrite } from "@/app/auth";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { accounts, channels, dlrPolicies, customers, PROTOCOL, TRAFFIC_TYPE, type Account, type AccountInput } from "@/app/master";
import { idOptions, loadingOf, nameOf, useAccountBinds, useList } from "@/app/queries";
import { usePage } from "@/app/usePage";
import { Alert, Button, EmptyState, FILTER_W, Input as TextInput, PageHeader, Select, Table, TableWrap, Th, Toolbar } from "@/ui";
import { AccountForm } from "./AccountForm";
import { accountDraft, accountInput } from "./customerDraft";

/** 抽屉里正在编辑的账号：null 表示新建，undefined 表示抽屉关闭。 */
type Editing = Account | null | undefined;

/** 客户账号：接入账号、凭证与 IP 白名单，以及 SMPP 账号实际在线的会话（D178），可按客户筛选；新建与编辑在右侧抽屉里（docs/04 第 14 节）。 */
export function Accounts() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [editing, setEditing] = useState<Editing>(undefined);
  const customerList = useList("customers", customers);
  const accountList = useList("accounts", accounts);
  const channelList = useList("channels", channels);
  const policyList = useList("dlr-policies", dlrPolicies);
  const binds = useAccountBinds(); // 实际在线的会话，smpp-server 心跳上报（决策 D178）

  const keyword = q.trim().toLowerCase();
  const shown = (accountList.data ?? []).filter((a) => (!customerFilter || String(a.customer_id) === customerFilter) && (!keyword || a.name.toLowerCase().includes(keyword) || a.system_id.toLowerCase().includes(keyword)));
  const page = usePage("accounts", shown, [keyword, customerFilter]);
  const error = accountList.error ?? customerList.error;

  return (
    <>
      <PageHeader title="客户账号" description="客户的接入账号、凭证与 IP 白名单，保存即生效；路由绑定在“账号路由”管理。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing(null)}>新建账号</Button>} />
      {error && <Alert tone="danger" title="加载失败">{error.message}</Alert>}
      <Toolbar actions={(q || customerFilter) && <ClearFilters onClear={() => { setQ(""); setCustomerFilter(""); }} />}>
        <TextInput size="sm" className="ax-search" placeholder="搜索账号名称、system_id" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select size="sm" width={FILTER_W.select} ariaLabel="客户" options={[{ value: "", label: "全部客户" }, ...idOptions(customerList.data)]} value={customerFilter} onChange={setCustomerFilter} />
      </Toolbar>
      <TableWrap loading={loadingOf(accountList)}>
        {shown.length ? (
          <>
            <Table>
              <thead><tr><Th w="id">ID</Th><Th w={160}>system_id</Th><Th w="name">名称</Th><Th w="name">客户</Th><Th w={72}>协议</Th><Th w={72}>类型</Th><Th numeric w={80}>TPS</Th><Th w={120}>绑定状态</Th><Th w="status">状态</Th><Th /></tr></thead>
              <tbody>
                {page.rows.map((a) => (
                  <tr key={a.id}>
                    <td className="ax-mono">{a.id}</td><td className="ax-mono">{a.system_id}</td><td>{a.name}</td><td>{nameOf(customerList.data, a.customer_id)}</td>
                    <td>{PROTOCOL[a.protocol]}</td><td>{TRAFFIC_TYPE[a.traffic_type]}</td><td className="ax-num">{a.tps}</td>
                    <td><BindStatusPill status={accountBindStatus(a, binds.data, binds.isError)} /></td>
                    <td><EntityStatusPill status={a.status} /></td>
                    <td><Button variant="ghost" size="sm" onClick={() => setEditing(a)}>{canWrite ? "编辑" : "查看"}</Button><Link className="ax-btn ax-btn-ghost ax-btn-sm" to={`/account-routing/${a.id}`}>账号路由</Link></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...page} />
          </>
        ) : (
          !accountList.isPending && <EmptyState title="没有账号">账号是客户的接入凭证，一个账号只支持一种协议（决策 D10）。</EmptyState>
        )}
      </TableWrap>
      {editing !== undefined && (
        <EntityDrawer title={editing ? `编辑账号 ${editing.system_id}` : "新建账号"} initial={accountDraft(editing ?? undefined)} convert={(d) => accountInput(d, !editing)}
          save={(body: AccountInput) => (editing ? accounts.update(editing.id, body) : accounts.create(body))}
          invalidate={["accounts"]} message={editing ? "已更新账号" : "已新建账号"} deletion={editing ? deleteTarget("账号", editing.system_id, accounts, editing.id, ["accounts", "profit-guards", "alert-thresholds", "account-routing", "sell-prices", "balances"]) : undefined} onClose={() => setEditing(undefined)}>
          {(f) => <AccountForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing} customers={customerList.data} channels={channelList.data} policies={policyList.data} />}
        </EntityDrawer>
      )}
    </>
  );
}
